/**
 * SectionCommentPanel - TASK-374: CO Review System
 *
 * Comment input and list for a specific KYC review section.
 * Supports severity levels, optional field path targeting, and comment resolution.
 */

import React, { useState } from 'react';
import {
  MessageSquarePlus,
  Info,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Loader2,
  Send,
  FileSearch,
} from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../../../utils/logger';
import { addComment, resolveComment } from '../../../services/reviewService';
import { formatDateTime } from '../../../utils/formatters';
import type { KYCReviewComment, CommentSeverity } from '../../../types/complianceWorkflow';

interface SectionCommentPanelProps {
  reviewId: string;
  sectionId: string;
  comments: KYCReviewComment[];
  onCommentAdded: () => void;
}

const SEVERITY_OPTIONS: { value: CommentSeverity; label: string; icon: React.ElementType; color: string; bg: string }[] = [
  { value: 'info', label: 'Info', icon: Info, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' },
  { value: 'warning', label: 'Warning', icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-50 border-yellow-200' },
  { value: 'action_required', label: 'Action Required', icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
];

const getSeverityConfig = (severity: CommentSeverity) => {
  return SEVERITY_OPTIONS.find((opt) => opt.value === severity) || SEVERITY_OPTIONS[0];
};

const SectionCommentPanel: React.FC<SectionCommentPanelProps> = ({
  reviewId,
  sectionId,
  comments,
  onCommentAdded,
}) => {
  const [commentText, setCommentText] = useState('');
  const [severity, setSeverity] = useState<CommentSeverity>('info');
  const [fieldPath, setFieldPath] = useState('');
  const [showFieldPath, setShowFieldPath] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const sectionComments = comments.filter((c) => c.section_id === sectionId);

  const handleSubmit = async () => {
    if (!commentText.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    try {
      setSubmitting(true);
      await addComment(reviewId, {
        section_id: sectionId,
        field_path: fieldPath.trim() || undefined,
        comment: commentText.trim(),
        severity,
      });
      toast.success('Comment added');
      setCommentText('');
      setFieldPath('');
      setSeverity('info');
      setShowFieldPath(false);
      onCommentAdded();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add comment';
      toast.error(message);
      logger.error('Failed to add comment:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolve = async (commentId: string) => {
    try {
      setResolvingId(commentId);
      await resolveComment(reviewId, commentId);
      toast.success('Comment resolved');
      onCommentAdded();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to resolve comment';
      toast.error(message);
      logger.error('Failed to resolve comment:', err);
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Comment input */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquarePlus className="w-4 h-4 text-gray-500" />
          <h4 className="text-sm font-medium text-gray-700">Add Comment</h4>
        </div>

        {/* Severity selector */}
        <div className="flex items-center gap-2 mb-3">
          {SEVERITY_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            return (
              <button
                key={opt.value}
                onClick={() => setSeverity(opt.value)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  severity === opt.value
                    ? opt.bg
                    : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                <Icon className={`w-3.5 h-3.5 ${severity === opt.value ? opt.color : ''}`} />
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Comment textarea */}
        <textarea
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          placeholder="Add your review comment..."
          rows={3}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />

        {/* Optional field path */}
        {showFieldPath && (
          <div className="mt-2">
            <input
              type="text"
              value={fieldPath}
              onChange={(e) => setFieldPath(e.target.value)}
              placeholder="Field path (e.g., first_name, tax_id)"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        )}

        {/* Actions row */}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFieldPath(!showFieldPath)}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                showFieldPath
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              <FileSearch className="w-3 h-3" />
              {showFieldPath ? 'Hide field path' : 'Target field'}
            </button>

            {/* Placeholder for TASK-375 integration */}
            <button
              onClick={() => toast.info('Document request feature coming in TASK-375')}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-500 hover:bg-gray-100 transition-colors"
            >
              Request Document
            </button>
          </div>

          <button
            onClick={handleSubmit}
            disabled={!commentText.trim() || submitting}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5" />
            )}
            Add Comment
          </button>
        </div>
      </div>

      {/* Comments list */}
      {sectionComments.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider">
            Comments ({sectionComments.length})
          </h4>

          {sectionComments.map((comment) => {
            const config = getSeverityConfig(comment.severity);
            const Icon = config.icon;

            return (
              <div
                key={comment.id}
                className={`rounded-lg border p-3 ${
                  comment.is_resolved ? 'bg-gray-50 border-gray-200 opacity-70' : config.bg
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    <Icon
                      className={`w-4 h-4 mt-0.5 shrink-0 ${
                        comment.is_resolved ? 'text-gray-400' : config.color
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm ${
                          comment.is_resolved ? 'text-gray-500 line-through' : 'text-gray-900'
                        }`}
                      >
                        {comment.comment}
                      </p>

                      {comment.field_path && (
                        <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600 font-mono">
                          {comment.field_path}
                        </span>
                      )}

                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        <span>{comment.created_by_name || 'Unknown'}</span>
                        <span>{formatDateTime(comment.created_at)}</span>
                        {comment.is_resolved && comment.resolved_by_name && (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="w-3 h-3" />
                            Resolved by {comment.resolved_by_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {!comment.is_resolved && (
                    <button
                      onClick={() => handleResolve(comment.id)}
                      disabled={resolvingId === comment.id}
                      className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 rounded hover:bg-green-100 disabled:opacity-50 transition-colors"
                    >
                      {resolvingId === comment.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <CheckCircle className="w-3 h-3" />
                      )}
                      Resolve
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {sectionComments.length === 0 && (
        <div className="text-center py-4 text-gray-400">
          <p className="text-xs">No comments for this section yet</p>
        </div>
      )}
    </div>
  );
};

export default SectionCommentPanel;
